import { Component, Inject, Input, EventEmitter, Output, ViewChild } from '@angular/core';

import { BehaviorSubject, Subject, combineLatest, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { MultiFilterableSelectComponent } from '../../../../shared/filterable-select/components/multi-filterable-select.component';

import { AntenneService } from '../../antenne/services/antenne.service';
import { GroupementService } from '../services/groupement.service';
import { GroupementTLR } from '../entities/groupement-tlr.entity';

@Component({
	selector: 'groupement-filterable-selector',
	templateUrl: 'groupement-filterable-selector.html',
})
export class GroupementFilterableSelectorComponent {
	groupements: GroupementTLR[] = [];
	selectedGroupement: GroupementTLR | undefined = undefined;

	getGroupementLabel = (groupement: GroupementTLR) => (groupement.libelle ? groupement.libelle : '') + ' (' + groupement.code + ')';
	
	agenceIdSubject = new BehaviorSubject<number | null | undefined>(undefined);
	antenneIdSubject = new BehaviorSubject<number | null | undefined>(undefined);
	selectedGroupementIdSubject = new Subject<number | null | undefined>();

	constructor( //
		@Inject(AntenneService) antenneService: AntenneService, //
		@Inject(GroupementService) gpService: GroupementService, //
	) {
		// A la reception d'un nouveau numero d'antenne, recuperer les groupements rattaches
		let groupements$ = combineLatest([this.agenceIdSubject, this.antenneIdSubject]).pipe(
			switchMap(
				([agenceId, antenneId]) =>  {
					if (antenneId != null && antenneId != undefined) {
						return gpService.getGroupementTLRByAntenne(antenneId);
					} else if (agenceId != null && agenceId != undefined) {
						return antenneService.getAntenneTLRByAgence(agenceId).pipe(
							switchMap(
								(antennes) => {
									let gpLists$ = antennes.map(
										(antenne) => gpService.getGroupementTLRByAntenne(antenne.id)
									);
									
									return forkJoin(gpLists$);
								}
							),
							map(
								(gpLists) => {
									let groupements: GroupementTLR[] = [];
									gpLists.forEach(
										(gpList) => gpList.forEach(
											(groupement) => groupements.push(groupement)
										)
									);
									groupements.sort(
										(gp1, gp2) => gp1.code.localeCompare(gp2.code)
									);
									return groupements;
								}
							)
						);
					} else {
						return gpService.getGroupementTLRList();
					}
				}
			)
		);
		
		// Une fois les groupements chargees les transmettre au sous-composant
		groupements$.subscribe(
			(groupements : GroupementTLR[]) => {
				if (groupements) {
					groupements.sort( (p1, p2) => p1.code.localeCompare(p2.code) );
					this.groupements = groupements;
				}
			}
		);

		// Definit comment combiner les donnees sur l'ensemble des parcs connues avec un id d'parc pour fixer l'parc actuellement selectionnee 
		combineLatest([groupements$, this.selectedGroupementIdSubject]).subscribe(
			([groupements, groupementId]) => {
				let newSelectedGroupement: GroupementTLR | undefined = undefined; 
				if (groupementId) {
					let groupement = groupements.find(groupement => groupement.id === groupementId);
					newSelectedGroupement = groupement;
				}
				
				if (newSelectedGroupement != this.selectedGroupement) {
					this.selectedGroupement = newSelectedGroupement;
					this.onSelectedGroupementChange(this.selectedGroupement);
				}
			}
		);
	}

	@ViewChild(MultiFilterableSelectComponent) input!: MultiFilterableSelectComponent;

	@Input() id!: string;
	@Input() disabled = false;
	
	// Classes pour l'affichege
	public _mandatory: boolean = false;
	@Input() set mandatory(value: boolean | undefined) {
		if (value) {
			this._mandatory = value;
		}
	}
	get mandatory(): boolean | undefined {
		return this._mandatory;
	}
	public _labelClass: string = 'col-md-1';
	@Input() set labelClass(value: string | undefined) {
		if (value) {
			this._labelClass = value;
		}	
	}
	get labelClass(): string | undefined {
		return this._labelClass;
	}
	
	public _fieldClass: string = 'col-md-7';
	@Input() set fieldClass(value: string | undefined) {
		if (value) {
			this._fieldClass = value;
		}
	}
	get fieldClass(): string | undefined {
		return this._fieldClass;
	}

	@Input() set agenceId(id: number | null | undefined) {
		this.agenceIdSubject.next(id);
	}
	
	@Input() set antenneId(id: number | null | undefined) {
		this.antenneIdSubject.next(id);
	}
	
	private _selectedGroupementId: number | null | undefined = null;
	@Input() set selectedGroupementId(id: number | null | undefined) {
		this._selectedGroupementId = id;
		this.selectedGroupementIdSubject.next(id);
	}
	get selectedGroupementId(): number | null | undefined {
		return this._selectedGroupementId;
	}
	
	@Output() selectedGroupementIdChange = new EventEmitter<number | null>();
	
	@Output() selectedGroupementChange = new EventEmitter<GroupementTLR | null>();
	
	onSelectedGroupementChange(groupement: GroupementTLR | null | undefined) {
		let id = groupement ? groupement.id : null;
		this._selectedGroupementId = id;
		this.selectedGroupementIdChange.emit(id);
		this.selectedGroupementChange.emit(groupement);
	}
}